import React, { useState, useEffect } from 'react';
import CountryFilter from '../components/CountryFilter';
import TripCard from '../components/TripCard';

const HERO_IMAGES = [
  { src: '/images/rome-hero-1.jpg', city: 'Rome' },
  { src: '/images/paris-hero-1.jpg', city: 'Paris' },
  { src: '/images/istanbul-hero-1.jpg', city: 'Istanbul' },
  { src: '/images/amsterdam-hero-1.jpg', city: 'Amsterdam' },
  { src: '/images/granada-hero-1.jpg', city: 'Granada' },
  { src: '/images/marrakesh-hero-2.jpg', city: 'Marrakesh' },
];

const COMING_SOON = [
  {
    country: 'Italy',
    region: 'Tuscany',
    title: 'Tuscany in 10 Days',
    description: 'Rolling hills, Renaissance art, and world-class wine through Florence, Siena, and the Val d\'Orcia.',
    coverImage: 'https://travelmaker-artefacts.s3.eu-north-1.amazonaws.com/city-it-flo_1.jpg',
  },
  {
    country: 'Japan',
    region: 'Kansai',
    title: 'Kyoto \u0026 Beyond',
    description: 'Ancient temples, bamboo groves, and traditional ryokans in Japan\'s cultural heartland.',
    coverImage: 'https://travelmaker-artefacts.s3.eu-north-1.amazonaws.com/city-jp-kyo_20.jpg',
  },
  {
    country: 'Greece',
    region: 'Attica \u0026 Cyclades',
    title: 'Athens \u0026 the Islands',
    description: 'Classical ruins, Mediterranean sunsets, and island-hopping through the Aegean.',
    coverImage: 'https://travelmaker-artefacts.s3.eu-north-1.amazonaws.com/city-gr-ath_6.jpg',
  },
];

const Trips = () => {
  const [trips, setTrips] = useState([]);
  const [activeCountry, setActiveCountry] = useState('All');
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    fetch('/itineraries/index.json')
      .then((res) => res.json())
      .then((data) => {
        setTrips(data.trips || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Auto-play carousel every 5s
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((i) => (i + 1) % HERO_IMAGES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const countries = [...new Set(trips.map((t) => t.country))].sort();

  const filteredTrips =
    activeCountry === 'All'
      ? trips
      : trips.filter((t) => t.country === activeCountry);

  return (
    <>
      <main className="bg-gradient-to-b from-white via-gray-50/30 to-white pt-0">
        {/* Hero Carousel */}
        <section className="relative h-screen overflow-hidden">
          {/* Background images */}
          {HERO_IMAGES.map((img, i) => (
            <div
              key={img.src}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                i === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={img.src}
                alt={img.city}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20" />
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(0,0,0,0.5),transparent_70%)]" />
            </div>
          ))}

          {/* Overlay text — centered */}
          <div className="absolute inset-0 z-10 flex flex-col justify-end items-center px-8 pb-40">
            <div className="max-w-xl text-center">
              <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-medium mb-6">
                <span className="w-2 h-2 bg-white rounded-full mr-2" />
                Expertly crafted itineraries
              </div>

              <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight drop-shadow-lg whitespace-nowrap">
                TravelMaker Trips
              </h1>

              <p className="text-xl text-white/90 leading-relaxed drop-shadow">
                Day-by-day itineraries built by our team, with audio-guided depth for every stop.
              </p>
            </div>
          </div>

          {/* Dot indicators */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {HERO_IMAGES.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentSlide(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === currentSlide ? 'w-8 bg-white' : 'w-1.5 bg-white/50'
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        </section>

        {/* Country Filter + Trip Grid */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-5">
            {/* Filter */}
            <div className="mb-10">
              <CountryFilter
                countries={countries}
                activeCountry={activeCountry}
                onChange={setActiveCountry}
              />
            </div>

            {/* Loading */}
            {loading && (
              <div className="flex justify-center py-20">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {/* Trip Grid */}
            {!loading && filteredTrips.length > 0 && (
              <div
                className={`grid gap-8 ${
                  filteredTrips.length === 1
                    ? 'grid-cols-1 max-w-xl mx-auto'
                    : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                }`}
              >
                {filteredTrips.map((trip) => (
                  <TripCard key={trip.slug} trip={trip} />
                ))}
              </div>
            )}

            {/* Empty state */}
            {!loading && filteredTrips.length === 0 && (
              <div className="text-center py-20">
                <p className="text-gray-500 text-lg mb-2">No trips found for this country yet.</p>
                <p className="text-gray-400 text-sm">Check back soon — we are adding new destinations every month.</p>
              </div>
            )}
          </div>
        </section>

        {/* Coming Soon */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-5">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                More Destinations Coming Soon
              </h2>
              <div className="w-24 h-1 bg-primary mx-auto rounded-full mb-4" />
              <p className="text-gray-600 max-w-2xl mx-auto">
                Our team is crafting detailed itineraries across Europe and Asia. Here is a preview of what is next.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {COMING_SOON.map((item) => (
                <div
                  key={item.title}
                  className="group bg-gray-50 rounded-2xl overflow-hidden border border-gray-100"
                >
                  <div className="relative h-48 overflow-hidden grayscale opacity-70">
                    <img
                      src={item.coverImage}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gray-900/30" />
                    <div className="absolute top-4 left-4">
                      <span className="inline-block bg-white/90 text-gray-800 text-xs font-semibold px-3 py-1 rounded-full">
                        {item.country}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-medium bg-gray-200 text-gray-500 px-2.5 py-1 rounded-full">
                        Coming Soon
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-700 mb-2">{item.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-24 bg-gradient-to-br from-primary via-primary to-blue-600 text-white text-center overflow-hidden">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
            <div className="absolute bottom-0 right-0 w-80 h-80 bg-white/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
          </div>

          <div className="relative z-10 max-w-4xl mx-auto px-5">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium mb-8">
              <span className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse" />
              Take your trip with you
            </div>

            <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              Ready to Explore?
            </h2>

            <p className="text-xl opacity-90 leading-relaxed max-w-2xl mx-auto mb-10">
              Download TravelMaker to access audio guides, maps, and curated activities for every stop on your journey.
            </p>

            <div className="flex gap-6 justify-center md:flex-row flex-col items-center">
              <a
                href="https://apps.apple.com/app/travelmaker-ai/id6742571605"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block transition-all duration-300 ease-in-out hover:-translate-y-1 hover:shadow-2xl"
              >
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg"
                  alt="Download on the App Store"
                  className="h-15 w-44 object-contain block"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.comrades.travelmaker"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block transition-all duration-300 ease-in-out hover:-translate-y-1 hover:shadow-2xl"
              >
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                  alt="Get it on Google Play"
                  className="h-15 w-44 object-contain block"
                />
              </a>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Trips;